/**
 * Which parts of a help page or release note belong to a switchable feature
 * (Settings → Features), so they disappear with it.
 *
 * - A whole file: `feature: proposals` in its frontmatter.
 * - One `##` section: a `<!-- feature: materials -->` line inside it.
 *
 * Mirrored in `apps/api/src/lib/help/feature-sections.ts`, which filters what
 * BESTie reads; keep the two in step.
 */

const MARKER = /^\s*<!--\s*feature:\s*([a-z-]+)\s*-->\s*$/;
const SECTION_HEADING = /^##\s/;

/**
 * Drops every `##` section whose feature is off, and every marker line. Text
 * before the first `##` heading always stays.
 *
 * @param markdown - The body, frontmatter already removed.
 * @param isOn - Whether a feature key is on; unknown keys should answer true.
 * @returns The body without switched-off sections, and whether any section is left.
 */
export function withoutFeatureSections(
  markdown: string,
  isOn: (feature: string) => boolean,
): { readonly markdown: string; readonly sectionsLeft: boolean } {
  const lines = markdown.split('\n');
  const kept: string[] = [];
  let section: string[] = [];
  let inSection = false;
  let sectionsLeft = false;
  let sectionOn = true;

  const flush = (): void => {
    if (inSection && sectionOn) {
      kept.push(...section);
      sectionsLeft = true;
    }
    section = [];
    sectionOn = true;
  };

  for (const line of lines) {
    if (SECTION_HEADING.test(line)) {
      flush();
      inSection = true;
      section.push(line);
      continue;
    }
    const marker = MARKER.exec(line);
    if (marker) {
      if (inSection && !isOn(marker[1] ?? '')) sectionOn = false;
      continue;
    }
    if (inSection) section.push(line);
    else kept.push(line);
  }
  flush();
  return { markdown: kept.join('\n').replace(/\n{3,}/g, '\n\n'), sectionsLeft };
}
